import { useEffect, useState } from "react";
import { Star, Lock, Play, Radio, FlaskConical } from "lucide-react";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { toast } from "sonner";

export default function PaidStrategies() {
  const [strategies, setStrategies] = useState([]);
  const [deployments, setDeployments] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [brokerCapabilities, setBrokerCapabilities] = useState([]);
  const [strategyModes, setStrategyModes] = useState({});
  const [brokerIds, setBrokerIds] = useState({});
  const [multipliers, setMultipliers] = useState({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [catalog, brokerRes, capabilityRes] = await Promise.all([
        api.get("/special-strategies"),
        api.get("/brokers"),
        api.get("/brokers/capabilities"),
      ]);

      const catalogStrategies = catalog.data || [];

      const statusResults = await Promise.all(
        catalogStrategies.map(async (strategy) => {
          try {
            const res = await api.get(
              `/special-strategies/${strategy.slug}/status`
            );
            return {
              slug: strategy.slug,
              deployments: res.data || [],
            };
          } catch {
            return {
              slug: strategy.slug,
              deployments: [],
            };
          }
        })
      );

      const deploymentMap = {};
      statusResults.forEach(({ slug, deployments }) => {
        deploymentMap[slug] = deployments;
      });

      setStrategies(catalogStrategies);
      setDeployments(deploymentMap);
      setBrokers(
        (brokerRes.data || []).filter(
          (b) => b.status === "connected" || b.connected === true
        )
      );

      setBrokerCapabilities(
        capabilityRes?.data?.brokers || []
      );
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Could not load paid strategies"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const requiredExchangeForStrategy = (strategy) => {
    const underlying = String(
      strategy?.underlying || ""
    ).toUpperCase();

    if (
      underlying === "SENSEX" ||
      strategy?.slug === "kiran_sensex_selling_bash"
    ) {
      return "BFO";
    }

    return "NFO";
  };

  const liveBrokersForStrategy = (strategy) => {
    const requiredExchange =
      requiredExchangeForStrategy(strategy);

    return brokerCapabilities.filter(
      (b) =>
        b.connected === true &&
        b.live_ready === true &&
        b.adapter_implemented === true &&
        Array.isArray(b.exchanges) &&
        b.exchanges.includes(requiredExchange)
    );
  };

  const deploy = async (slug) => {
    const strategyMode = strategyModes[slug] || "paper";

    const strategyBrokerId = brokerIds[slug] || "";
    const strategyMultiplier = multipliers[slug] ?? 1;

    if (strategyMode === "live") {
      if (!strategyBrokerId) {
        toast.error("Select a connected broker for Live Trading");
        return;
      }

      const ok = window.confirm(
        "LIVE TRADING CONFIRMATION\n\n" +
        `${slug} will be allowed to place REAL ` +
        `${String(
          strategies.find((x) => x.slug === slug)?.underlying ||
          (slug === "kiran_sensex_selling_bash"
            ? "SENSEX"
            : "NIFTY")
        )} option orders in your selected broker account.\n\n` +
        "Continue?"
      );

      if (!ok) return;
    }

    setBusy(true);

    try {
      await api.post(`/special-strategies/${slug}/deploy`, {
        mode: strategyMode,
        broker_connection_id:
          strategyMode === "live" ? strategyBrokerId : null,
        multiplier: Number(strategyMultiplier) || 1,
      });

      toast.success(
        (strategyModes[slug] || "paper") === "live"
          ? "Live strategy deployed"
          : "Paper strategy deployed"
      );

      await load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        "Deployment failed"
      );
    } finally {
      setBusy(false);
    }
  };

  const isStrategyRunning = (slug) =>
    (deployments[slug] || []).some(
      (d) => d.status === "running"
    );

  return (
    <Layout>
      <PageHeader
        title="PAID STRATEGIES"
        subtitle="Proprietary automated strategies"
      />

      <div className="p-6 max-w-6xl mx-auto">

        {loading ? (
          <div className="text-sm text-slate-400">
            Loading paid strategies…
          </div>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-6">
              <Star size={25} />
              <div>
                <h1 className="text-2xl font-bold">
                  PAID STRATEGIES
                </h1>
                <p className="text-sm text-slate-500">
                  Proprietary strategies available to authorized accounts.
                </p>
              </div>
            </div>

            {strategies.map((strategy) => (
              <div
                key={strategy.slug}
                className="border border-slate-200 bg-white shadow-sm max-w-3xl"
              >

                <div className="p-6 border-b border-slate-200">

                  <div className="flex items-start justify-between">

                    <div>
                      <div className="flex items-center gap-2">
                        <Star size={19} />
                        <h2 className="font-bold text-xl">
                          {strategy.name}
                        </h2>
                      </div>

                      <p className="text-sm text-slate-500 mt-1">
                        {strategy.subtitle}
                      </p>
                    </div>

                    <Lock size={19} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 mt-6 text-sm">

                    {strategy.slug === "kiran_nifty_selling_bash" && (
                      <>
                        <div>
                          <b>Underlying:</b> NIFTY
                        </div>

                        <div>
                          <b>Strategy:</b> Positional
                        </div>

                        <div>
                          <b>Entry:</b> Monday 1:00 PM IST
                        </div>

                        <div>
                          <b>Reference:</b> Previous trading-day NIFTY close
                        </div>

                        <div>
                          <b>Bullish:</b> ≥ +50 points → SELL OTM-5 CE
                        </div>

                        <div>
                          <b>Bearish:</b> ≤ −50 points → SELL OTM-5 PE
                        </div>

                        <div>
                          <b>No trade:</b> Between −50 and +50
                        </div>

                        <div>
                          <b>Expiry:</b> Next Tuesday expiry
                        </div>

                        <div>
                          <b>Holding:</b> Overnight
                        </div>

                        <div>
                          <b>Stop Loss:</b> 2× Entry Premium
                        </div>

                        <div>
                          <b>Exit:</b> Tuesday 3:15 PM IST
                        </div>
                      </>
                    )}

                    {strategy.slug === "kiran_sensex_selling_bash" && (
                      <>
                        <div>
                          <b>Underlying:</b> SENSEX
                        </div>

                        <div>
                          <b>Strategy:</b> Positional
                        </div>

                        <div>
                          <b>Entry:</b> Wednesday 1:00 PM IST
                        </div>

                        <div>
                          <b>Reference:</b> Previous trading-day SENSEX close
                        </div>

                        <div>
                          <b>Bullish:</b> ≥ +200 points → SELL OTM-10 CE
                        </div>

                        <div>
                          <b>Bearish:</b> ≤ −200 points → SELL OTM-10 PE
                        </div>

                        <div>
                          <b>No trade:</b> Between −200 and +200
                        </div>

                        <div>
                          <b>Expiry:</b> Thursday expiry
                        </div>

                        <div>
                          <b>Holding:</b> Overnight
                        </div>

                        <div>
                          <b>Stop Loss:</b> 2× Entry Premium
                        </div>

                        <div>
                          <b>Exit:</b> Thursday 3:28 PM IST
                        </div>
                      </>
                    )}

                    {strategy.slug === "kiran_ema_pullback_bash" && (
                      <>
                        <div>
                          <b>Underlying:</b> NIFTY
                        </div>

                        <div>
                          <b>Strategy:</b> Intraday
                        </div>

                        <div>
                          <b>Timeframe:</b> 5-minute
                        </div>

                        <div>
                          <b>Signal:</b> EMA50 / EMA100 / EMA200 pullback
                        </div>

                        <div>
                          <b>Bullish:</b> EMA50 &gt; EMA100 &gt; EMA200 → BUY CE
                        </div>

                        <div>
                          <b>Bearish:</b> EMA50 &lt; EMA100 &lt; EMA200 → BUY PE
                        </div>

                        <div>
                          <b>Option Selection:</b> Premium ₹450–₹500
                        </div>

                        <div>
                          <b>Subsequent Setup:</b> One strike deeper ITM
                        </div>

                        <div>
                          <b>Target:</b> +10 premium points
                        </div>

                        <div>
                          <b>Stop Loss:</b> −5 premium points
                        </div>
                      </>
                    )}

                  </div>

                </div>

                <div className="p-6 bg-slate-50">

                  <div className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-4">
                    Deployment
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Mode
                      </label>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setStrategyModes((prev) => ({
                              ...prev,
                              [strategy.slug]: "paper",
                            }));
                          }}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border ${
                            (strategyModes[strategy.slug] || "paper") === "paper"
                              ? "bg-amber-50 border-amber-300 text-amber-700"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          <FlaskConical size={15} />
                          Paper
                        </button>

                        <button
                          type="button"
                          onClick={() => {
                            setStrategyModes((prev) => ({
                              ...prev,
                              [strategy.slug]: "live",
                            }));
                          }}
                          className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm border ${
                            (strategyModes[strategy.slug] || "paper") === "live"
                              ? "bg-emerald-50 border-emerald-300 text-emerald-700"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          <Radio size={15} />
                          Live
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Broker
                      </label>

                      <select
                        value={brokerIds[strategy.slug] || ""}
                        onChange={(e) => {
                          setBrokerIds((prev) => ({
                            ...prev,
                            [strategy.slug]: e.target.value,
                          }));
                        }}
                        disabled={(strategyModes[strategy.slug] || "paper") !== "live"}
                        className="w-full px-3 py-2 text-sm border border-slate-200 bg-white disabled:bg-slate-100"
                      >
                        <option value="">
                          {(strategyModes[strategy.slug] || "paper") === "live"
                            ? "Select connected broker"
                            : "Not required for Paper"}
                        </option>

                        {liveBrokersForStrategy(strategy).map((b) => (
                          <option
                            key={b.connection_id}
                            value={b.connection_id}
                          >
                            {b.label ||
                              b.broker_name ||
                              b.broker}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">
                        Lots Multiplier
                      </label>

                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={multipliers[strategy.slug] ?? 1}
                        onChange={(e) => {
                          setMultipliers((prev) => ({
                            ...prev,
                            [strategy.slug]: e.target.value,
                          }));
                        }}
                        className="w-full px-3 py-2 text-sm border border-slate-200 bg-white"
                      />
                    </div>

                  </div>

                  {(strategyModes[strategy.slug] || "paper") === "live" && (
                    <div className="mt-4 border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
                      Live mode can place real NIFTY option orders.
                      Verify broker connection, capital and strategy
                      settings before confirming deployment.
                    </div>
                  )}

                  <button
                    type="button"
                    disabled={
                      busy ||
                      isStrategyRunning(strategy.slug)
                    }
                    onClick={() => deploy(strategy.slug)}
                    className={`mt-5 inline-flex items-center gap-2 px-5 py-2.5 text-sm text-white disabled:opacity-50 ${
                      (strategyModes[strategy.slug] || "paper") === "live"
                        ? "bg-emerald-700 hover:bg-emerald-800"
                        : "bg-slate-900 hover:bg-slate-800"
                    }`}
                  >
                    <Play size={15} />

                    {isStrategyRunning(strategy.slug)
                      ? "Already Deployed"
                      : (strategyModes[strategy.slug] || "paper") === "live"
                      ? "Deploy Live Strategy"
                      : "Deploy Paper Strategy"}
                  </button>

                </div>

              </div>
            ))}

            {deployments.length > 0 && (
              <div className="max-w-3xl mt-8">

                <h2 className="font-bold text-lg mb-3">
                  My Paid Strategy Deployments
                </h2>

                <div className="border border-slate-200 bg-white">

                  {deployments.map((d) => (
                    <div
                      key={d.id}
                      className="flex items-center justify-between p-4 border-b last:border-b-0"
                    >

                      <div>
                        <div className="font-semibold">
                          {d.strategy_name}
                        </div>

                        <div className="text-xs text-slate-500 mt-1">
                          {String(d.mode || "").toUpperCase()} ·{" "}
                          {d.status}
                        </div>
                      </div>

                      <div className="text-sm">
                        {d.broker_name ||
                          ((strategyModes[strategy.slug] || "paper") === "live"
                            ? "Broker"
                            : "Paper Trading")}
                      </div>

                    </div>
                  ))}

                </div>

              </div>
            )}

          </>
        )}

      </div>
    </Layout>
  );
}
